<template>
  <div class="tag-control-container">
    <!-- Standard tags section -->
    <div class="tagsection">
      <div 
        class="tag-wrapper" 
        v-for="(tag, i) in standardTags" 
        :key="'std-' + i"
        @mouseenter="hoveredTag = tag.type"
        @mouseleave="hoveredTag = null"
      >
        <div class="icon-column">
          <img
            :src="getIcon(tag.type)"
            alt=""
            :width="size"
            :height="size"
            class="tag-media"
            :class="{ 'tag-media--hovered': hoveredTag === tag.type }"
          />
          <div 
            v-if="hasType(tag)"
            class="tag-title"
            :class="[titleClass, { 'tag-title--active': hoveredTag === tag.type }]"
          >
            {{ tag.type }}
          </div>
        </div>

        <span 
          class="tag-desc"
          :style="`font-size: ${size / 2}px`" 
          v-if="hasDesc(tag)"
        >
          {{ formatDesc(tag) }}
        </span>
      </div>
    </div>

    <!-- Custom tags section -->
    <div class="tagsection">
      <div 
        class="tag-wrapper" 
        v-for="(tag, i) in customTags" 
        :key="'cust-' + i"
        @mouseenter="hoveredTag = tag.type"
        @mouseleave="hoveredTag = null"
      >
        <div class="icon-column">
          <div
            class="tag-media"
            :class="{ 'tag-media--hovered': hoveredTag === tag.type }"
            :style="`line-height: 1; padding: 0; font-size: ${size * 1}px;`"
          >
            {{ tag.emoji }}
          </div>
          <div 
            v-if="hasType(tag)"
            class="tag-title"
            :class="[titleClass, { 'tag-title--active': hoveredTag === tag.type }]"
          >
            {{ tag.type }}
          </div>
        </div>

        <span 
          class="tag-desc"
          :style="`font-size: ${size / 2}px`" 
          v-if="hasDesc(tag)"
        >
          {{ formatDesc(tag) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  tags: {
    type: Array,
    default: () => []
  },
  size: {
    type: Number,
    default: 160
  },
  hoverAndClickStyling: Boolean,
  titleClass: {
    type: String,
    default: ''
  },
  percentageMode: {
    type: Boolean,
    default: false
  },
  grandTotal: {
    type: Number,
    default: null
  }
})

const hoveredTag = ref(null)

const standardTags = computed(() => {
  return (props.tags || []).filter(t => !t.custom || t.custom === false)
})

const customTags = computed(() => {
  return (props.tags || []).filter(t => t.custom === true)
})

const referenceTotal = computed(() => {
  if (props.grandTotal !== null) return props.grandTotal
  if (!props.tags || !props.tags.length) return 0
  const numericSum = props.tags.reduce((sum, item) => {
    const val = Number(item.desc)
    return sum + (isNaN(val) ? 0 : val)
  }, 0)
  return numericSum > 0 ? numericSum : props.tags.length
})

function hasDesc(tag) {
  return tag.desc !== undefined && tag.desc !== null && tag.desc !== ''
}

function hasType(tag) {
  return tag.type !== undefined && tag.type !== null && tag.type !== ''
}

function formatDesc(tag) {
  if (!props.percentageMode) {
    return tag.desc
  }
  const numericVal = Number(tag.desc)
  if (isNaN(numericVal) || referenceTotal.value === 0) {
    return tag.desc
  }
  const rawPercentage = Math.round((numericVal / referenceTotal.value) * 100)
  const percentage = numericVal > 0 ? Math.max(1, rawPercentage) : 0
  return `${percentage}%`
}

function getIcon(modelType) {
  try {
    return require(`../../assets/icons/${modelType.toLowerCase()}.png`)
  } catch (error) {
    console.log(modelType + ' has no icon: ' + error)
  }
}
</script>

<style scoped lang="scss">
.tag-control-container {
  position: relative;
}

.nav__emoji {
  font-size: 900%;
}

.tagsection {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem 1.25rem;
  position: relative;
  align-items: flex-start;
  padding-bottom: 1.5rem;
}

.tag-wrapper {
  display: inline-flex;
  flex-direction: row;
  align-items: flex-start;
  user-select: none;
  cursor: pointer;
}

.icon-column {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.tag-media {
  display: block;
  object-fit: contain;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center center;

  &--hovered {
    transform: scale(1.08);
  }
}

.tag-desc {
  margin-left: 0.5rem;
  align-self: center;
  line-height: 1;
}

.tag-title {
  margin-top: 6px;
  font-size: 11.5px;
  font-weight: 400;
  line-height: 1.2;
  color: #ffffff;
  background-color: rgba(43, 48, 60, 0.85);
  padding: 3px 8px;
  border-radius: 5px;
  white-space: nowrap;
  letter-spacing: 0.01em;
  text-align: center;
  transform-origin: center top;
  transition: background-color 0.22s ease,
              color 0.22s ease,
              border-color 0.22s ease,
              box-shadow 0.22s ease,
              transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  &--active {
    background-color: #00b8a3;
    color: #fff8f0;
    border-color: #006ad4;
    transform: scale(1.05);
    box-shadow: 0 3px 10px rgba(184, 98, 0, 0.35);
  }
}
</style>